import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import {
    IconArrowLeftOutline,
    IconArrowRightOutline,
    IconReload,
    Text,
} from '@zillow/constellation';

// components
import { ImageContainer } from 'app/shared/components/photos/ImageContainer';

import {
    StyledCloseButton,
    StyledThumbItem,
} from './StyledComponents';

const propTypes = {
    index: PropTypes.number.isRequired,
    isMobileScreenResolution: PropTypes.bool,
    item: PropTypes.object.isRequired,
    isFirstItem: PropTypes.bool,
    isLastItem: PropTypes.bool,
    isListingPhotoProcessing: PropTypes.bool,
    isDisabled: PropTypes.bool,
    // actions
    handleNextClick: PropTypes.func.isRequired,
    removePhoto: PropTypes.func.isRequired,
    rotatePhoto: PropTypes.func.isRequired,
};

const defaultProps = {
    isListingPhotoProcessing: false,
    isDisabled: false,
    isMobileScreenResolution: true,
    isFirstItem: false,
    isLastItem: false,
};

function ThumbItem({
    index,
    isMobileScreenResolution,
    item,
    isFirstItem,
    isLastItem,
    isListingPhotoProcessing,
    isDisabled,
    // actions
    handleItemClick,
    handlePrevClick,
    handleNextClick,
    removePhoto,
    rotatePhoto,
}) {
    const caption = `${index + 1}. ${item.caption || ''}`;
    const thumbUrl = item.url ? item.url.replace('http://photo', 'https://photonet') : '';

    const keyDownHandler = handler => (e) => {
        // 13 === enter 32 === spacebar
        if (e.keyCode === 13 || e.keyCode === 32) {
            handler(index);
        }
    };

    const thisHandleItemClick = () => {
        handleItemClick(index);
    };

    const thisHandleNextClick = () => {
        handleNextClick(index);
    };

    const thisHandlePrevClick = () => {
        handlePrevClick(index);
    };

    const handleRemovePhotoClick = (event) => {
        event.stopPropagation();
        removePhoto(index);
    };

    const handleRotatePhoto = () => {
        rotatePhoto(index);
    };

    // ThumbItem component gets cloned on drag.  Must add brand prefixes to maintain font style.
    return (
        <StyledThumbItem disabled={isListingPhotoProcessing}>
            <div className="ThumbItem-img-container">
                <ImageContainer
                    onClick={thisHandleItemClick}
                    onKeyDown={keyDownHandler(thisHandleItemClick)}
                    image={thumbUrl}
                    isBackgroundImage={true}
                />

                <div
                    aria-label="Image caption"
                    tabIndex={0}
                    role="button"
                    className="ThumbItem-caption"
                    onClick={thisHandleItemClick}
                    onKeyDown={keyDownHandler(thisHandleItemClick)}
                >
                    <Text as="div" fontType="body" fontColor="white">{caption}</Text>
                </div>

                {!isDisabled && (
                    <StyledCloseButton
                        aria-label="Delete Photo"
                        className="Action Action-close"
                        onClick={handleRemovePhotoClick}
                        onKeyDown={keyDownHandler(handleRemovePhotoClick)}
                    />
                )}
            </div>

            {isMobileScreenResolution && (
                <div className="ThumbItem-actions-container">
                    <IconArrowLeftOutline
                        aria-label="Previous Image"
                        tabIndex={0}
                        role="button"
                        className={cx(
                            'Action',
                            { disabled: (isFirstItem || isListingPhotoProcessing || isDisabled) }
                        )}
                        size="sm"
                        fontColor="brand"
                        onClick={thisHandlePrevClick}
                        onKeyDown={keyDownHandler(thisHandlePrevClick)}
                    />

                    <IconReload
                        aria-label="Rotate Image"
                        tabIndex={0}
                        role="button"
                        className={cx(
                            'Action',
                            { disabled: (isListingPhotoProcessing || isDisabled) }
                        )}
                        size="sm"
                        fontColor="brand"
                        onClick={handleRotatePhoto}
                        onKeyDown={keyDownHandler(handleRotatePhoto)}
                    />

                    <IconArrowRightOutline
                        aria-label="Next Image"
                        tabIndex={0}
                        role="button"
                        className={cx(
                            'Action',
                            { disabled: (isLastItem || isListingPhotoProcessing || isDisabled) }
                        )}
                        size="sm"
                        fontColor="brand"
                        onClick={thisHandleNextClick}
                        onKeyDown={keyDownHandler(thisHandleNextClick)}
                    />
                </div>
            )}
        </StyledThumbItem>
    );
}

ThumbItem.propTypes = propTypes;
ThumbItem.defaultProps = defaultProps;

export default ThumbItem;
